/**
 * @file System.AiUsage.types.ts
 * @description Types for per-user and per-org AI usage metering and caps.
 *
 * Backs the `parameters/aiUsage` org config doc, the per-user
 * `users/{uid}/aiUsage/{windowId}` counters, and the org rollup at
 * `parameters/aiUsage/rollups/{windowId}`. Consumed by the metering provider
 * wrapper (`meteredProvider`, `recordAiUsage`) and the `settings/usage` UI.
 *
 * Usage is metered in a single abstracted unit — credits. Each provider call's
 * native usage (tokens / calls / pages) is converted to credits by a
 * per-concrete-model weight in the server-side metering layer (`computeCredits`);
 * the client only ever sees credits. A scope with no cap for a window is uncapped.
 */

import type { Timestamp } from 'firebase/firestore';
import { DocDataWithId } from './System.types';

/**
 * Credit caps for a single scope across the three windows. Absent fields mean
 * "no cap for that window".
 */
export type AiUsageCaps = {
    daily?: number;
    weekly?: number;
    monthly?: number;
};

/**
 * The `parameters/aiUsage` document. Read-only from the UI in this iteration.
 * All caps are expressed in credits.
 */
export type AiUsageParameters = DocDataWithId & {
    /** Default credit caps applied to every user. */
    perUser: AiUsageCaps;
    /** Org-wide credit rollup caps. */
    perOrg:  AiUsageCaps;
};

/**
 * A single counter window. Lives at:
 *  - `users/{uid}/aiUsage/{windowId}` for per-user counters
 *  - `parameters/aiUsage/rollups/{windowId}` for the org rollup
 *
 * `windowKey` matches the doc id and encodes the UTC window:
 * `daily-2026-05-15`, `weekly-2026-W20`, `monthly-2026-05`.
 */
export type AiUsageWindow = DocDataWithId & {
    window: 'daily' | 'weekly' | 'monthly';
    windowKey: string;
    credits: number;
    updatedAt: Timestamp;
};

/**
 * Idempotency record for one metered call, at
 * `parameters/aiUsage/recorded/{idempotencyKey}`. Written in the same
 * transaction that increments the counters, so its presence proves the call's
 * credits were already recorded — a redelivered Doc AI batch completion or a
 * retried sync parse reads it and skips re-incrementing. Never queried, only
 * get/set by doc id, so its unbounded accumulation (one doc per metered call,
 * never deleted) degrades no query. A TTL, if ever added, MUST exceed the
 * maximum event-redelivery/retry window, or an expired marker would re-open the
 * double-counting this record prevents.
 */
export type AiUsageRecordMarker = {
    idempotencyKey: string;
    credits: number;
    recordedAt: Timestamp;
};

/**
 * UTC window-key derivation. Same canonical form is used for the Firestore
 * doc id (`users/{uid}/aiUsage/{key}` and `parameters/aiUsage/rollups/{key}`)
 * and the stored `windowKey` field. Both server (enforcement, increment) and
 * client (settings UI) import from here so the bucket they read can never
 * diverge from the bucket the server wrote.
 *
 * Daily:   `daily-YYYY-MM-DD`
 * Weekly:  `weekly-YYYY-Www`         (ISO week — week 1 contains the year's first Thursday)
 * Monthly: `monthly-YYYY-MM`
 */
export interface WindowKeys {
    daily: string;
    weekly: string;
    monthly: string;
}

const padWin = (n: number): string => n.toString().padStart(2, '0');

const dailyKey = (now: Date): string =>
    `daily-${now.getUTCFullYear()}-${padWin(now.getUTCMonth() + 1)}-${padWin(now.getUTCDate())}`;

const monthlyKey = (now: Date): string =>
    `monthly-${now.getUTCFullYear()}-${padWin(now.getUTCMonth() + 1)}`;

/** ISO week (year + week number) for a UTC date. Week 1 contains the first Thursday. */
export const isoWeekFromUtc = (date: Date): { year: number; week: number } => {
    const tmp = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dayNum = tmp.getUTCDay() === 0 ? 7 : tmp.getUTCDay();
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((tmp.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7);
    return { year: tmp.getUTCFullYear(), week };
};

const weeklyKey = (now: Date): string => {
    const { year, week } = isoWeekFromUtc(now);
    return `weekly-${year}-W${padWin(week)}`;
};

export const currentWindowKeys = (now: Date): WindowKeys => ({
    daily:   dailyKey(now),
    weekly:  weeklyKey(now),
    monthly: monthlyKey(now),
});

/** Mon..Sun (UTC) of the ISO week that contains `now`. Used by the settings UI label. */
export const isoWeekBoundsUtc = (now: Date): { monday: Date; sunday: Date } => {
    const { year, week } = isoWeekFromUtc(now);
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));
    const monday = new Date(week1Monday);
    monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    return { monday, sunday };
};

/**
 * Thrown by the enforcement gate when a cap would be exceeded. Surfaced as
 * HTTP 429 from `/runlanguagemodel` (non-stream paths) or as an SSE
 * `aiUsageLimitExceeded` event whose data shape is
 * `AiUsageLimitExceededPayload` (stream paths, since headers are already
 * sent). The client reconstructs the typed error in both cases.
 */
export class AiUsageLimitExceededError extends Error {
    readonly window: 'daily' | 'weekly' | 'monthly';
    readonly scope: 'user' | 'org';

    constructor(params: { window: 'daily' | 'weekly' | 'monthly'; scope: 'user' | 'org' }) {
        super(`AI usage limit exceeded: ${params.scope} ${params.window} credit allowance`);
        this.name = 'AiUsageLimitExceededError';
        this.window = params.window;
        this.scope = params.scope;
    }
}

/** Wire shape for the typed limit-exceeded payload (HTTP 429 body and SSE data). */
export interface AiUsageLimitExceededPayload {
    error: 'ai_usage_limit_exceeded';
    window: 'daily' | 'weekly' | 'monthly';
    scope: 'user' | 'org';
}

/** Canonical SSE event name for the typed limit-exceeded error on streaming endpoints. */
export const AI_USAGE_LIMIT_EXCEEDED_EVENT = 'aiUsageLimitExceeded';

export const isAiUsageLimitExceededPayload = (value: unknown): value is AiUsageLimitExceededPayload => {
    if (!value || typeof value !== 'object') return false;
    const v = value as Partial<AiUsageLimitExceededPayload>;
    return v.error === 'ai_usage_limit_exceeded'
        && (v.window === 'daily' || v.window === 'weekly' || v.window === 'monthly')
        && (v.scope === 'user' || v.scope === 'org');
};
